import {
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";

const KEY = "gridaction.float.";
const MOVE_BEFORE_DRAG = 6;

interface Offset {
  x: number;
  y: number;
}

function load(id: string): Offset {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY + id) ?? "null") as Offset | null;
    if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) return saved;
  } catch {
    // Storage can be blocked; the button just starts in its usual place.
  }
  return { x: 0, y: 0 };
}

function save(id: string, offset: Offset) {
  try {
    localStorage.setItem(KEY + id, JSON.stringify(offset));
  } catch {
    // Not remembering the spot is fine.
  }
}

/**
 * Lets a finger or mouse drag whatever it wraps anywhere on screen. A tap still clicks; a drag
 * never does. It starts see-through so it doesn't hide the page, goes solid when touched, and
 * remembers where it was left. The offset is from the button's usual place.
 */
export function Draggable({
  id,
  children,
  className,
  onDragStart,
}: {
  id: string;
  children: ReactNode;
  className?: string;
  /** Called once when a press turns into a drag; a plain tap never calls it. */
  onDragStart?: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState<Offset>(() => load(id));
  const [dragging, setDragging] = useState(false);
  const latest = useRef(offset);
  const swallowClick = useRef(false);

  const place = useCallback((next: Offset) => {
    latest.current = next;
    setOffset(next);
  }, []);

  /** The room the button has: how far it may move each way and still be fully on screen. */
  const limits = useCallback((): {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } | null => {
    const el = box.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const homeLeft = rect.left - latest.current.x;
    const homeTop = rect.top - latest.current.y;
    return {
      minX: -homeLeft,
      maxX: window.innerWidth - homeLeft - rect.width,
      minY: -homeTop,
      maxY: window.innerHeight - homeTop - rect.height,
    };
  }, []);

  const clampTo = useCallback((want: Offset, room: ReturnType<typeof limits>): Offset => {
    if (!room) return want;
    return {
      x: Math.min(Math.max(want.x, room.minX), room.maxX),
      y: Math.min(Math.max(want.y, room.minY), room.maxY),
    };
  }, []);

  // After a rotation or a smaller window, pull a stranded button back into view.
  useEffect(() => {
    const keepOnScreen = () => place(clampTo(latest.current, limits()));
    keepOnScreen();
    window.addEventListener("resize", keepOnScreen);
    return () => window.removeEventListener("resize", keepOnScreen);
  }, [place, clampTo, limits]);

  /**
   * Moves are listened for on the whole window once a press starts, so the button keeps
   * following a mouse that has slipped off it. A press that never moves is just a tap.
   */
  const start = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const from = latest.current;
    const room = limits();
    const origin = { x: event.clientX, y: event.clientY };
    let moved = false;

    const move = (e: PointerEvent) => {
      const dx = e.clientX - origin.x;
      const dy = e.clientY - origin.y;
      if (!moved && Math.hypot(dx, dy) < MOVE_BEFORE_DRAG) return;
      if (!moved) onDragStart?.();
      moved = true;
      setDragging(true);
      place(clampTo({ x: from.x + dx, y: from.y + dy }, room));
    };
    const end = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      if (!moved) return;
      // The click that follows a drag must not press the button.
      swallowClick.current = true;
      setTimeout(() => {
        swallowClick.current = false;
      }, 0);
      setDragging(false);
      save(id, latest.current);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  };

  return (
    <div
      ref={box}
      data-dragging={dragging}
      className={cn("touch-none drop-shadow-md", className)}
      style={{ transform: `translate3d(${offset.x}px, ${offset.y}px, 0)` }}
      onPointerDown={start}
      onClickCapture={(event) => {
        if (swallowClick.current) {
          event.stopPropagation();
          event.preventDefault();
        }
      }}
    >
      {children}
    </div>
  );
}
